import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation, Navigate } from 'react-router-dom';

import { useAppDispatch } from '../../store';
import { authService } from '../../services/auth.service';
import { setCurrentRoute, setAuthorized, setLoading } from '../../store/slices/global.slice';

import { AuthError } from '../AuthError';
import { AuthLoading } from '../AuthLoading';
import { AccessDenied } from '../AccessDenied';
import { Header } from '../layout';
import { AuthStateRendererProps } from './Auth.types';

/**
 * Renders conditional UI based on authentication state.
 *
 * Displays different screens for:
 * - Loading: Authentication in progress
 * - Auth Error: Authentication failed
 * - Access Denied: User not authorized
 * - Unauthorized: User not authenticated or authorized
 * - Success: Renders app layout with header and protected routes
 *
 * Also handles route access validation and current route tracking in Redux.
 *
 */
export const AuthStateRenderer = ({
  loading,
  authError,
  accessDenied,
  isAuthenticated,
  isAuthorized,
  user,
  errorMessage,
  children
}: AuthStateRendererProps) => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setCurrentRoute(location.pathname));
    // Clear loading state when route changes
    dispatch(setLoading(false));

    if (isAuthenticated && user) {
      const currentPath = location.pathname;
      if (currentPath !== '/' && !authService.canAccessRoute(currentPath)) {
        window.location.href = '/pricing-regional';
        return;
      }

      if (!isAuthorized) {
        dispatch(setAuthorized(true));
      }
    }
  }, [location.pathname, dispatch, isAuthenticated, user, isAuthorized]);

  useEffect(() => {
    // Clear loading state when component unmounts
    return () => {
      dispatch(setLoading(false));
    };
  }, [dispatch]);

  if (loading) {
    return <AuthLoading message="Авторизація..." />;
  }

  if (authError) {
    return <AuthError message={errorMessage} />;
  }

  if (accessDenied) {
    return <AccessDenied />;
  }

  if (!isAuthenticated || !isAuthorized) {
    return <AuthLoading message={!isAuthenticated ? 'Авторизація...' : 'Перевірка доступу користувача...'} />;
  }

  const ProtectedRoute = ({ children, path }: { children: React.ReactNode; path: string }) => {
    if (!authService.canAccessRoute(path)) {
      return <Navigate to="/pricing-regional" replace />;
    }
    return <>{children}</>;
  };

  return (
    <Box className="app-layout">
      <Header user={user} />
      <Box component="main" className="app-main" sx={{ bgcolor: 'background.default' }}>
        {children(ProtectedRoute)}
      </Box>
    </Box>
  );
};
